import { useCallback, useLayoutEffect, useRef, useState } from 'react';
export function useTimelineScroll(identity, visible = true, positions, continuity, history) {
    const viewportRef = useRef(null);
    const contentRef = useRef(null);
    const following = useRef(true);
    const anchor = useRef();
    const expectedScroll = useRef();
    const lastScrollTop = useRef(0);
    const lastGeometry = useRef({ scrollHeight: 0, clientHeight: 0 });
    const draggingScrollbar = useRef(false);
    const touchY = useRef();
    const currentIdentity = useRef();
    const pendingScrollTop = useRef();
    const positionsRef = useRef(positions);
    positionsRef.current = positions;
    const continuityRef = useRef(continuity);
    continuityRef.current = continuity;
    const isVisible = useRef(visible);
    isVisible.current = visible;
    const [showLatest, setShowLatest] = useState(false);
    const latestVisible = useRef(false);
    const historyRef = useRef(history);
    historyRef.current = history;
    const loading = useRef();
    const attemptedCursor = useRef();
    const historyIntent = useRef(false);
    const [historyState, setHistoryState] = useState();
    function prefetchHistory() {
        const viewport = viewportRef.current;
        const source = historyRef.current;
        if (!viewport || !isVisible.current || following.current || !historyIntent.current || !source?.hasOlder || !source.cursor || loading.current)
            return;
        if (viewport.scrollTop > Math.max(600, viewport.clientHeight * 1.5) || attemptedCursor.current === source.cursor)
            return;
        attemptedCursor.current = source.cursor;
        void loadOlder(source.load).catch(() => { });
    }
    function updateLatest(viewport) {
        const next = !following.current && viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 64;
        if (latestVisible.current === next)
            return;
        latestVisible.current = next;
        setShowLatest(next);
    }
    function captureReadingContinuity(scrollTop) {
        const continuity = continuityRef.current;
        if (!continuity)
            return;
        const previous = continuity.positions.get(continuity.identity);
        continuity.positions.set(continuity.identity, {
            following: following.current,
            scrollTop: scrollTop ?? previous?.scrollTop,
        });
    }
    function captureAnchor() {
        const viewport = viewportRef.current;
        if (!viewport || !viewport.clientHeight)
            return;
        const top = viewport.getBoundingClientRect().top;
        const entry = Array.from(viewport.querySelectorAll('[data-entry-key]')).find((element) => element.getBoundingClientRect().bottom > top);
        if (!entry?.dataset.entryKey) {
            // A reconnect can temporarily remove entries before the same epoch returns.
            if (!positionsRef.current)
                anchor.current = undefined;
            captureReadingContinuity();
            return;
        }
        anchor.current = { key: entry.dataset.entryKey, offset: entry.getBoundingClientRect().top - top };
        if (currentIdentity.current !== undefined)
            positionsRef.current?.set(currentIdentity.current, {
                following: following.current, anchor: anchor.current,
            });
        captureReadingContinuity(viewport.scrollTop);
    }
    function updatePosition() {
        const viewport = viewportRef.current;
        if (!viewport || !isVisible.current || !viewport.clientHeight)
            return;
        if (following.current) {
            viewport.scrollTop = viewport.scrollHeight;
        }
        else if (anchor.current) {
            const saved = anchor.current;
            const entry = Array.from(viewport.querySelectorAll('[data-entry-key]')).find((element) => element.dataset.entryKey === saved.key);
            if (entry)
                viewport.scrollTop += entry.getBoundingClientRect().top - viewport.getBoundingClientRect().top - saved.offset;
        }
        else if (pendingScrollTop.current !== undefined && viewport.querySelector('[data-entry-key]')) {
            viewport.scrollTop = pendingScrollTop.current;
            pendingScrollTop.current = undefined;
        }
        expectedScroll.current = viewport.scrollTop;
        lastScrollTop.current = viewport.scrollTop;
        lastGeometry.current = { scrollHeight: viewport.scrollHeight, clientHeight: viewport.clientHeight };
        captureAnchor();
        updateLatest(viewport);
    }
    const updateRef = useRef(updatePosition);
    updateRef.current = updatePosition;
    useLayoutEffect(() => {
        if (currentIdentity.current !== identity) {
            currentIdentity.current = identity;
            attemptedCursor.current = undefined;
            historyIntent.current = false;
            loading.current = undefined;
            const saved = positionsRef.current?.get(identity);
            const continuity = continuityRef.current?.positions.get(continuityRef.current.identity);
            following.current = saved?.following ?? continuity?.following ?? true;
            anchor.current = saved?.anchor;
            pendingScrollTop.current = !saved && !following.current ? continuity?.scrollTop : undefined;
            expectedScroll.current = undefined;
            draggingScrollbar.current = false;
        }
        updatePosition();
    });
    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (!viewport || !content || typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(() => updateRef.current());
        observer.observe(viewport);
        observer.observe(content);
        return () => observer.disconnect();
    }, []);
    function pauseFollowing() {
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        following.current = false;
        expectedScroll.current = undefined;
        lastScrollTop.current = viewport.scrollTop;
        captureAnchor();
    }
    useLayoutEffect(() => () => { loading.current = undefined; currentIdentity.current = undefined; }, []);
    useLayoutEffect(() => {
        const end = () => { draggingScrollbar.current = false; };
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
        return () => {
            window.removeEventListener('pointerup', end);
            window.removeEventListener('pointercancel', end);
        };
    }, []);
    const onScroll = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || !isVisible.current || !viewport.clientHeight)
            return;
        if (expectedScroll.current !== undefined && Math.abs(viewport.scrollTop - expectedScroll.current) < 1)
            return;
        if (following.current) {
            const geometryChanged = viewport.scrollHeight !== lastGeometry.current.scrollHeight || viewport.clientHeight !== lastGeometry.current.clientHeight;
            // Resizing keeps the latest content visible; browser control navigation keeps its chosen position.
            if (viewport.scrollTop >= lastScrollTop.current || (!draggingScrollbar.current && geometryChanged)) {
                updateRef.current();
                return;
            }
            following.current = false;
            historyIntent.current = draggingScrollbar.current;
        }
        else if (viewport.scrollTop > lastScrollTop.current && viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 64) {
            following.current = true;
        }
        expectedScroll.current = undefined;
        lastScrollTop.current = viewport.scrollTop;
        captureAnchor();
        updateLatest(viewport);
        prefetchHistory();
    }, []);
    function scrollToLatest() {
        draggingScrollbar.current = false;
        historyIntent.current = false;
        following.current = true;
        updatePosition();
    }
    function loadOlder(action) {
        if (loading.current && loading.current.identity === currentIdentity.current)
            return loading.current.promise;
        const requestIdentity = currentIdentity.current ?? identity;
        captureAnchor();
        following.current = false;
        captureReadingContinuity();
        attemptedCursor.current = historyRef.current?.cursor;
        setHistoryState({ identity: requestIdentity, pending: true });
        const promise = Promise.resolve().then(() => {
            if (currentIdentity.current === requestIdentity && loading.current?.promise === promise)
                return action();
        }).catch((error) => {
            if (currentIdentity.current === requestIdentity && loading.current?.promise === promise)
                setHistoryState({ identity: requestIdentity, pending: false, error: error instanceof Error ? error.message : 'Earlier activity could not be loaded.' });
            throw error;
        }).finally(() => {
            if (loading.current?.promise !== promise)
                return;
            loading.current = undefined;
            if (currentIdentity.current === requestIdentity)
                setHistoryState((state) => state?.identity === requestIdentity ? { ...state, pending: false } : state);
        });
        loading.current = { identity: requestIdentity, promise };
        return promise;
    }
    function onPointerDown(event) {
        const viewport = event.currentTarget;
        if (event.pointerType !== 'mouse' || event.target !== viewport || event.clientX < viewport.getBoundingClientRect().right - 16)
            return;
        draggingScrollbar.current = true;
    }
    function onWheel(event) {
        if (event.deltaY < 0) {
            historyIntent.current = true;
            pauseFollowing();
            prefetchHistory();
        }
    }
    function onKeyDown(event) {
        if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable="true"]'))
            return;
        const movesUp = ['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey && event.target === event.currentTarget);
        if (movesUp) {
            historyIntent.current = true;
            pauseFollowing();
            prefetchHistory();
        }
    }
    function onTouchMove(event) {
        const y = event.touches[0]?.clientY;
        if (y !== undefined && touchY.current !== undefined && y > touchY.current) {
            historyIntent.current = true;
            pauseFollowing();
            prefetchHistory();
        }
        touchY.current = y;
    }
    function onFocus(event) {
        if (event.target === event.currentTarget)
            return;
        const viewport = event.currentTarget;
        const bounds = viewport.getBoundingClientRect();
        const control = event.target.getBoundingClientRect();
        if (viewport.scrollTop < lastScrollTop.current || control.top < bounds.top || control.bottom > bounds.bottom)
            pauseFollowing();
    }
    return {
        viewportRef, contentRef, onScroll, showLatest, scrollToLatest, loadOlder,
        historyLoading: historyState?.identity === identity && historyState.pending,
        historyError: historyState?.identity === identity ? historyState.error : undefined,
        onWheel, onPointerDown, onKeyDown, onFocus,
        onTouchStart: (event) => { touchY.current = event.touches[0]?.clientY; },
        onTouchMove,
    };
}
//# sourceMappingURL=useTimelineScroll.js.map